/**
 * This source code is quoted from rc-slider.
 * homepage: https://github.com/react-component/slider
 */
import React, { useState, useEffect, useCallback, useRef, useMemo, useImperativeHandle, forwardRef } from 'react';
import warning from 'warning';
import createSlider from './common/createSlider';
import Track from './common/Track';
import Handle from './Handle';
import * as utils from './utils';
import type {SliderProps} from './iSlider'

// 使用 forwardRef 以便 createSlider 可以访问组件方法
const Slider = forwardRef<any, SliderProps>((props, ref) => {
    // 从 props 中解构出 createSlider 注入的方法
    const {
        calcValueByPos: injectedCalcValueByPos,
        calcOffset: injectedCalcOffset,
        saveHandle: injectedSaveHandle,
        removeDocumentEvents: injectedRemoveDocumentEvents,
        ...restProps
    } = props as any;

    // 使用 ref 存储不经常变化的 props，避免 useCallback 依赖项过多
    const propsRef = useRef(restProps);
    useEffect(() => {
        propsRef.current = restProps;
    }, [restProps]);
    /* static propTypes = {
	    ...sliderPropTypes,
	    defaultValue: PropTypes.number,
	    value: PropTypes.number,
	    disabled: PropTypes.bool,
	}; */

    // 使用 ref 存储注入的方法，确保引用稳定
    // 初始化时不设置默认值，在使用时再判断
    const calcValueByPosRef = useRef<((value: number) => number) | undefined>(injectedCalcValueByPos);
    const calcOffsetRef = useRef<((value: number) => number) | undefined>(injectedCalcOffset);
    const saveHandleRef = useRef<((index: number, h: any) => void) | undefined>(injectedSaveHandle);
    const removeDocumentEventsRef = useRef<(() => void) | undefined>(injectedRemoveDocumentEvents);

    // 生成唯一的 sliderKey
    const sliderKey = useMemo(() => new Date().getTime().toString(), []);

    // trimAlignValue 函数定义 - 优化：使用 ref 减少依赖
    const trimAlignValue = useCallback((v: number, nextProps: Partial<SliderProps> = {}) => {
        const mergedProps = {...propsRef.current, ...nextProps};
        const val = utils.ensureValueInRange(v, mergedProps);
        return utils.ensureValuePrecision(val, mergedProps);
    }, []);

    // 初始化 state
    const getInitialValue = () => {
        const defaultValue = restProps.defaultValue !== undefined ?
            restProps.defaultValue : restProps.min;
        const value = restProps.value !== undefined ?
            restProps.value : defaultValue;
        return trimAlignValue(value);
    };

    const [value, setValue] = useState<number>(getInitialValue);
    const [dragging, setDragging] = useState<boolean>(false);

    // 组合 state 对象，保持与原 class 组件的兼容性
    const state = useMemo(() => ({
        value,
        dragging
    }), [value, dragging]);

    // 更新注入方法的引用 - 无条件更新，确保总是使用最新的
    useEffect(() => {
        calcValueByPosRef.current = injectedCalcValueByPos;
        calcOffsetRef.current = injectedCalcOffset;
        saveHandleRef.current = injectedSaveHandle;
        removeDocumentEventsRef.current = injectedRemoveDocumentEvents;
    }, [injectedCalcValueByPos, injectedCalcOffset, injectedSaveHandle, injectedRemoveDocumentEvents]);

    // 开发环境警告
    useEffect(() => {
        // @ts-ignore Node环境变量
        if (process.env.NODE_ENV !== 'production') {
            warning(
                !('minimumTrackStyle' in restProps),
                'minimumTrackStyle will be deprecate, please use trackStyle instead.'
            );
            warning(
                !('maximumTrackStyle' in restProps),
                'maximumTrackStyle will be deprecate, please use railStyle instead.'
            );
        }
    }, []);

    // 使用 ref 中的方法，确保总是使用最新的引用
    const calcValueByPos = useCallback((position: number) => {
        if (calcValueByPosRef.current) {
            return calcValueByPosRef.current(position);
        }
        return 0; // 默认返回值
    }, []);

    const calcOffset = useCallback((value: number) => {
        if (calcOffsetRef.current) {
            return calcOffsetRef.current(value);
        }
        // 默认实现
        const {min = 0, max = 100} = propsRef.current;
        const ratio = (value - min) / (max - min);
        return ratio * 100;
    }, []);

    const saveHandle = useCallback((index: number, h: any) => {
        if (saveHandleRef.current) {
            saveHandleRef.current(index, h);
        }
    }, []);

    const removeDocumentEvents = useCallback(() => {
        if (removeDocumentEventsRef.current) {
            removeDocumentEventsRef.current();
        }
    }, []);

    const getValue = useCallback(() => {
        // 受控模式下直接返回props.value，避免状态更新延迟
        return 'value' in propsRef.current ? propsRef.current.value! : value;
    }, [value]);

    const getLowerBound = useCallback(() => {
        return propsRef.current.min;
    }, []);

    const getUpperBound = useCallback(() => {
        // 受控模式下直接返回props.value，避免状态更新延迟
        return 'value' in propsRef.current ? propsRef.current.value! : value;
    }, [value]);

    const onChange = useCallback((newState: { value: number }) => {
        const isNotControlled = !('value' in propsRef.current);
        if (isNotControlled) {
            setValue(newState.value);
        }
        // console.log("改变之后的："+newState.value);
        const changedValue = newState.value;
        propsRef.current.onChange?.(changedValue);
    }, []);

    const onStart = useCallback((position: number) => {
        // position = getMousePosition 或者 handlePosition
        // 1.getValue:return this.state.value
        // 2.onBeforeChange:输出开始变化之前的状态
        // 3.calcValueByPos：这个是在createSlider.jsx，超级多的方法混合在一起
        // 3.1 getSliderStart:slider的top或者left（主持或者水平）， pixelOffset = position - this.getSliderStart()得到mouseposition 或者 handlePosition - slider的top（或者left）
        // 3.2 this.calcValue(pixelOffset)：目的得到value，跟ratio有关
        // 3.3 this.trimAlignValue(this.calcValue(pixelOffset)):在slider.jsx中1.ensureValueInRange 先算出是否可取 v 或者 min 或者 max 2.ensureValuePrecision：来获取真的前进数值closest数值
        // 4.onChange：得到改变之后的值，setState{value}的值并且调用props.onChange
        setDragging(true);
        const prevValue = getValue();

        propsRef.current.onBeforeChange?.(prevValue);

        const newValue = calcValueByPos(position);

        if (newValue === prevValue) return;

        onChange({value: newValue});
    }, [getValue, calcValueByPos, onChange]);

    const onEnd = useCallback(() => {
        if (!dragging) return;
        setDragging(false);
        removeDocumentEvents();
        propsRef.current.onAfterChange?.(getValue());
    }, [dragging, getValue, removeDocumentEvents]);

    const onMove = useCallback((e: React.MouseEvent<HTMLDivElement> | React.TouchEvent<HTMLDivElement>, position: number) => {
        utils.pauseEvent(e);
        const oldValue = value;
        const newValue = calcValueByPos(position);
        if (newValue === oldValue) return;
        onChange({value: newValue});
    }, [value, calcValueByPos, onChange]);

    const onKeyboard = useCallback((e: React.KeyboardEvent<HTMLDivElement>) => {
        const valueMutator = utils.getKeyboardValueMutator(e);

        if (valueMutator) {
            utils.pauseEvent(e);
            const oldValue = value;
            const mutatedValue = valueMutator(oldValue, propsRef.current);
            const newValue = trimAlignValue(mutatedValue);
            if (newValue === oldValue) return;

            onChange({value: newValue});
        }
    }, [value, trimAlignValue, onChange]);

    const clearSliderValue = useCallback(() => {
        // 只通过 onChange 更新，避免重复调用 setValue
        const isNotControlled = !('value' in propsRef.current);
        if (isNotControlled) {
            setValue(0);
        }
        propsRef.current.onChange?.(0);
        propsRef.current.onAfterChange?.(0);
    }, []);

    // 替代 componentDidUpdate，监听 props 变化
    const prevPropsRef = useRef<SliderProps>(restProps);
    useEffect(() => {
        const prevProps = prevPropsRef.current;

        // 检查相关属性是否发生变化
        if (
            restProps.value === prevProps.value &&
            restProps.min === prevProps.min &&
            restProps.max === prevProps.max &&
            restProps.defaultValue === prevProps.defaultValue
        ) {
            return;
        }

        // 如果有控制属性(value)或边界属性(min/max)或默认值属性变化时才处理
        if (!('value' in restProps || 'min' in restProps || 'max' in restProps || 'defaultValue' in restProps)) return;

        const prevValue = value;
        let newValue: number;

        // 优先使用 value，如果没有则使用当前值
        if (restProps.value !== undefined) {
            newValue = restProps.value;
        } else {
            // 如果是非控制组件且defaultValue发生变化，使用新的defaultValue
            if (restProps.defaultValue !== prevProps.defaultValue && restProps.defaultValue !== undefined) {
                newValue = restProps.defaultValue;
            } else {
                newValue = prevValue;
            }
        }

        const nextValue = trimAlignValue(newValue, restProps);
        if (nextValue === prevValue) return;

        setValue(nextValue);
        if (utils.isValueOutOfRange(newValue, restProps)) {
            restProps.onChange?.(nextValue);
        }

        prevPropsRef.current = restProps;
    }, [restProps.value, restProps.min, restProps.max, restProps.defaultValue, trimAlignValue]);

    // render 逻辑 - 生成 tracks 和 handles
    const {
        prefixCls,
        vertical,
        included,
        disabled,
        minimumTrackStyle,
        trackStyle,
        handleStyle,
        min,
        max,
        reverse,
        handle: handleGenerator,
        fieldid,
        dir: direction
    } = restProps;

    // 受控模式下使用props.value，避免状态更新延迟
    const currentValue = 'value' in restProps ? restProps.value! : value;
    const offset = calcOffset(currentValue);

    const handle = (handleGenerator || Handle.defaultHandle)({
        index: 0,
        className: `${prefixCls}-handle`,
        vertical,
        offset,
        value: currentValue,
        dragging,
        disabled,
        dir: direction,
        min,
        max,
        reverse,
        fieldid,
        style: (handleStyle as React.CSSProperties[])?.[0] || handleStyle,
        ref: (h: any) => saveHandle(0, h),
        // @ts-ignore
        domKey: `slider${sliderKey}`
    });

    const _trackStyle = (trackStyle as React.CSSProperties[])?.[0] || trackStyle;
    const track = (
        <Track
            className={`${prefixCls}-track`}
            vertical={vertical}
            reverse={reverse}
            included={included}
            offset={0}
            length={offset}
            fieldid={fieldid}
            dir={direction}
            style={{
                ...minimumTrackStyle,
                ..._trackStyle,
            }}
        />
    );

    // 暴露方法给 createSlider HOC
    // 关键修复：直接返回最新的方法，不通过ref（避免useEffect异步导致的延迟）
    useImperativeHandle(ref, () => ({
        calcValueByPos,
        calcOffset,
        saveHandle,
        removeDocumentEvents,
        onStart,
        onEnd,
        onMove,
        onKeyboard,
        onChange,
        getValue,
        getLowerBound,
        getUpperBound,
        trimAlignValue,
        clearSliderValue,
        state,
        setState: (newState: any) => {
            if (typeof newState === 'function') {
                const updatedState = newState(state);
                if ('value' in updatedState) setValue(updatedState.value);
                if ('dragging' in updatedState) setDragging(updatedState.dragging);
            } else {
                if ('value' in newState) setValue(newState.value);
                if ('dragging' in newState) setDragging(newState.dragging);
            }
        },
        // 直接返回最新计算的 tracks 和 handles
        tracks: track,
        handles: handle
    }), [
        calcValueByPos,
        calcOffset,
        saveHandle,
        removeDocumentEvents,
        onStart,
        onEnd,
        onMove,
        onKeyboard,
        onChange,
        getValue,
        getLowerBound,
        getUpperBound,
        trimAlignValue,
        clearSliderValue,
        state,
        track,
        handle
    ]);

    // Slider 组件本身不渲染任何内容，渲染由 createSlider HOC 处理
    return null;
});

Slider.displayName = 'Slider';

export default createSlider(Slider as any);
